import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import { navLinks } from '../UseManageTimes';

import useTimeData from '@usas/ui-web/state/time/UseTimeData';
import useMeetDistinctAthletesWithPersonIdData from '@usas/ui-web/state/meet/meetDistinctAthletes/UseMeetDistinctAthletesWithPersonIdData';

import { formatTimeForDisplay, formatTimeForFilterObject } from '@usas/ui-web/utils/TimesUtils';
import { isValidSwimTime } from '@usas/ui-web/utils/validation';

const useRelayLegs = () => {
  const navigate = useNavigate();
  const { timeState, putTime } = useTimeData();
  const { meetDistinctAthletesWithPersonIdState, getMeetDistinctAthletesWithPersonId } = useMeetDistinctAthletesWithPersonIdData();

  const [relayLegsState, setRelayLegsState] = useState({ relayLegs: [] });
  const [legErrorState, setLegErrorState] = useState({ isError: false, errorMessage: '' });

  const onSubmitFormCallback = (formState) => { }

  const createTimeObjForSubmit = () => {
    return {
      swimTimeId: timeState.objData.swimTimeId,
      personId: timeState.objData.personId,
      eventId: timeState.objData.eventId,
      meetId: timeState.objData.meetId,
      sessionId: timeState.objData.sessionId,
      heatNumber: timeState.objData.heatNumber,
      time: timeState.objData.time,
      swimDate: timeState.objData.swimDate,
      finishPosition: parseInt(timeState.objData.finishPosition),
      isLeadOffTime: timeState.objData.isLeadOffTime,
      seedTime: timeState.objData.seedTime,
      eventCompetitionGenderTypeId: timeState.objData.eventCompetitionGenderTypeId,
      teamName: timeState.objData.teamName,
      swimTimeRelay: formatLegsArrayForPut(relayLegsState.relayLegs),
      swimTimeSplit: [],
      laneNumber: timeState.objData.laneNumber,
      reactionTime: timeState.objData.reactionTime,
      lscCode: timeState.objData.lscCode,
      clubCode: timeState.objData.clubCode
    };
  };

  function timeAsSeconds(legTime) {
    if (legTime.indexOf(":") === -1) {
      return Number.parseFloat(legTime);
    }

    const [min, sec] = legTime.split(":");
    const val = Number.parseInt(min) * 60 + Number.parseFloat(sec);

    return val;
  }

  function getDuplicates(arr, key) {
    return arr.filter((item, index, self) =>
      index !== self.findIndex((t) => (
        t[key] === item[key]
      ))
    );
  }

  const areLegsInvalid = (relayLegs) => {
    let isInvalid = false;
    let errorMessage = '';

    let sumOfRelayLegsInSeconds = 0;
    let cntMale = 0;
    let cntFemale = 0;

    for (var i = 0, len = relayLegs.length; i < len; i++) {
      if (relayLegs[i].personName.includes('Male') === true) {
        cntMale++;
      } else {
        cntFemale++;
      }

      let min;
      let sec;
      if (relayLegs[i].legTime.split(":").length === 1) {
        min = undefined;
        sec = relayLegs[i].legTime;
      } else {
        const [mm, ss] = relayLegs[i].legTime.split(":");
        min = mm;
        sec = ss;
      }

      //no decimal
      if (sec.split(".").length === 1) {
        isInvalid = true;
        errorMessage = `Invalid Leg ${relayLegs[i].legNumber} Time`;


        return { isInvalid, errorMessage }
      }

      if (!isValidSwimTime(formatTimeForFilterObject(relayLegs[i].legTime))) {
        isInvalid = true;
        errorMessage = `Invalid Leg ${relayLegs[i].legNumber} Time`;

        return { isInvalid, errorMessage };
      }

      const [s, ms] = sec.split(".");
      if (min === undefined) {
        if (isNaN(s) === true || isNaN(ms) === true || s.length !== 2 || ms.length !== 2) {
          isInvalid = true;
          errorMessage = `Invalid Leg ${relayLegs[i].legNumber} Time`;

          return { isInvalid, errorMessage }
        }
      } else {
        if (
          isNaN(min) === true ||
          isNaN(s) === true ||
          isNaN(ms) === true ||
          (min.length !== 1 && ms.length !== 2) ||
          s.length !== 2 ||
          ms.length !== 2) {
          isInvalid = true;
          errorMessage = 'Invalid Leg Time';

          return { isInvalid, errorMessage }
        }
      }

      sumOfRelayLegsInSeconds += timeAsSeconds(relayLegs[i].legTime);
    }
    
    const eventGender = timeState.objData.eventCompetitionGenderType.typeCode;
    if (eventGender === 'MX') {
      if (((cntMale > 2 || cntFemale > 2) && (cntMale > 0 && cntFemale > 0)) || (cntMale === 4 || cntFemale === 4)) {
        isInvalid = true;
        errorMessage = 'Mixed Gender relays must have 2 males and 2 females';

        return { isInvalid, errorMessage }
      }
    }

    let relayTimeAsSeconds = timeAsSeconds(timeState.objData.time.substring(4, 11));

    if (sumOfRelayLegsInSeconds.toFixed(2) !== relayTimeAsSeconds.toFixed(2)) {
      isInvalid = true;
      errorMessage = `The sum of the relay leg times must equal the Swim Time (${formatTimeForDisplay(timeState.objData.time)})`;

      return { isInvalid, errorMessage };
    }

    const duplicates = getDuplicates(relayLegs, 'personId');

    if (duplicates.length > 0) {
      isInvalid = true;
      errorMessage = 'Duplicate Swimmers in Relay';

      return { isInvalid, errorMessage };
    }

    return { isInvalid, errorMessage };
  }

  const onSaveButtonClicked = (e) => {
    const { isInvalid, errorMessage } = areLegsInvalid(relayLegsState.relayLegs);

    if (isInvalid) {
      setLegErrorState({
        ...legErrorState,
        isError: true,
        errorMessage: errorMessage
      });
    } else {
      setLegErrorState({
        ...legErrorState,
        isError: false,
        errorMessage: ''
      });

      const timeObj = createTimeObjForSubmit();
      putTime(timeObj.swimTimeId, timeObj);
    }
  }

  const onCancelButtonClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    navigate(navLinks.DETAIL);
  }

  const onRowChanged = (value) => {
    let newArray = [...relayLegsState.relayLegs];

    for (let i = 0; i < newArray.length; i++) {
      if (newArray[i].legNumber === value.legNumber) {
        newArray[i] = { ...value };

        setRelayLegsState({
          ...relayLegsState,
          relayLegs: newArray
        });

        return;
      }
    }
  };

  const formatLegTimes = (swimTimeRelay, totalDistance) => {
    const formattedRelayLegTimes = swimTimeRelay.map((leg, index) => {
      return {
        swimTimeRelayId: leg.swimTimeRelayId,
        swimTimeId: leg.swimTimeId,
        personId: leg.personId,
        legEventId: leg.legEventId,
        legNumber: leg.legNumber,
        competitor: `${leg.person.firstName} ${leg.person.lastName}`,
        eventDescription: leg.legEvent.eventCode,
        legTime: formatTimeForDisplay(leg.legTime),
        reactionTime: leg.reactionTime
      };
    });
    return formattedRelayLegTimes;
  };

  const formatLegsArrayForPut = (legsArray) => {
    const legsToPut = legsArray.map((leg) => {
      return {
        swimTimeRelayId: leg.swimTimeRelayId,
        swimTimeId: leg.swimTimeId,
        personId: leg.personId,
        legEventId: leg.legEventId,
        legNumber: leg.legNumber,
        legTime: formatTimeForFilterObject(leg.legTime),
        reactionTime: leg.reactionTime
      };
    });

    return legsToPut;
  }

  function getEmptyRelayLegsArray(swimTimeId, distance) {
    //TODO: get course from timeState to determine number of splits for SCY etc.
    const numLegs = 4;
    let legsArray = [];

    for (let i = 0; i < numLegs; i++) {
      legsArray.push({
        swimTimeId: swimTimeId,
        legNumber: i + 1,
        competitor: '',
        eventDescription: '',
        legTime: ''
      });
    };

    return legsArray;
  }

  useEffect(() => {
    if (timeState.isObjLoaded) {
      getMeetDistinctAthletesWithPersonId(timeState.objData.meetId, timeState.objData.eventCompetitionGenderTypeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-de
  }, [timeState.isObjLoaded]);

  useEffect(() => {
    if (timeState.isObjLoaded) {
      let legsArray = [];
      if (timeState.objData.swimTimeRelay.length > 0) {
        legsArray = formatLegTimes(timeState.objData.swimTimeRelay, timeState.objData.event.distance);
      } else {
        if (legsArray.length === 0) {
          legsArray = getEmptyRelayLegsArray(timeState.objData.swimTimeId, timeState.objData.event.distance);
        }
      }

      setRelayLegsState({
        ...relayLegsState,
        relayLegs: legsArray
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeState.isObjLoaded]);

  return {
    timeState,
    relayLegsState,
    legErrorState,
    meetDistinctAthletesWithPersonIdState,
    onRowChanged,
    onSaveButtonClicked,
    onCancelButtonClicked,
    onSubmitFormCallback,
    setRelayLegsState
  };
}

export default useRelayLegs;